import React, { useState, useEffect } from 'react';
import { 
  AlertCircle, CheckCircle, Loader2, Tag, Info, CreditCard, 
  Shield, ArrowLeft, Percent, X, GraduationCap, Layers 
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { formFillApi } from '../../services/formFillApi'; 
import { useTheme } from '../Home/ThemeContext';

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const CheckoutPage = () => {
  const context = useTheme();
  const isDarkMode = context.isDarkMode || context.theme === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  
  const { applicationData, draftId, selectedColleges } = location.state || {};
  
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [couponError, setCouponError] = useState('');
  
  const [targetColleges, setTargetColleges] = useState(selectedColleges || applicationData?.targetColleges || []);
  
  // Fee Tier Logic — double fee kicks in when more than 5 colleges selected
  const collegeCount = targetColleges.length;
  const isDoubleFee = collegeCount > 5; 

  const [pricingConfig, setPricingConfig] = useState({
    basePrice: 0,
    processingFee: 0,
    platformFee: 0,
    totalPrice: 0
  });

  useEffect(() => {
    if (!applicationData || !draftId) {
      setError('⚠️ No application data found. Please fill the form first.');
      setTimeout(() => navigate('/student-form'), 2000);
      return;
    }

    if (targetColleges.length === 0) {
      setError('⚠️ No colleges selected. Please select colleges first.');
      setTimeout(() => navigate('/student/colleges'), 2000);
      return;
    }

    setApplication(applicationData);
    loadPricingData();
  }, [applicationData, draftId, targetColleges, navigate]);

  // FIX: tries 'application_fee' first (DB registrationType), 
  // fallbacks to 'student_registration' if no result or basePrice is 0.
  const loadPricingData = async () => {
    try {
      console.log("Fetching pricing from BACKEND...");
  
      const res = await formFillApi.getApplicationPricing('student_registration');
  
      console.log("PRICING RESPONSE:", res);
  
      const data = res?.data || res;
  
      if (!data) {
        throw new Error("No pricing found");
      }
  
      const rawBasePrice  = data.basePrice || 0;
      const processingFee = data.processingFee || 0;
      const platformFee   = data.platformFee || 0;
  
      const finalBasePrice = isDoubleFee ? rawBasePrice * 2 : rawBasePrice;
  
      setPricingConfig({
        basePrice: finalBasePrice,
        processingFee: processingFee,
        platformFee: platformFee,
        totalPrice: finalBasePrice + processingFee + platformFee
      });
  
    } catch (err) {
      console.error("Pricing error:", err);
      setError("Pricing load failed: " + err.message);
    } finally {
      setLoading(false);
    }
  };
  // --- STYLES ---
  const styles = {
    bg: isDarkMode ? 'bg-gradient-to-br from-black via-gray-900 to-black' : 'bg-gradient-to-br from-blue-50 via-white to-purple-50',
    textPrimary: isDarkMode ? 'text-white' : 'text-gray-900',
    textSecondary: isDarkMode ? 'text-gray-400' : 'text-gray-600',
    cardBg: isDarkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-100',
    inputBg: isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-purple-200 text-gray-900',
    stickySummary: isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-100',
  };

  // --- Calculations ---
  const BASE_FEE        = pricingConfig.basePrice;
  const PROCESSING_FEE  = pricingConfig.processingFee;
  const PLATFORM_FEE    = pricingConfig.platformFee;
  const GST_RATE        = 0.18;

  const subtotal = BASE_FEE;

  const discount = appliedCoupon 
    ? appliedCoupon.discountType === 'fixed' 
      ? appliedCoupon.discountValue 
      : Math.min((subtotal * appliedCoupon.discountValue) / 100, appliedCoupon.maxDiscount || Infinity)
    : 0;
  
  const amountAfterDiscount = subtotal - discount;
  const taxableAmount       = amountAfterDiscount + PROCESSING_FEE + PLATFORM_FEE;
  const gstAmount           = Math.round(taxableAmount * GST_RATE);
  const totalAmount         = taxableAmount + gstAmount;

  // --- Handlers ---
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    setIsValidatingCoupon(true);
    setCouponError('');
    try {
      const REGISTRATION_TYPE = "student_registration";

      const data = await formFillApi.validateCoupon({
        couponCode: couponCode.toUpperCase(),
        orderAmount: subtotal,
        registrationType: "student_registration"
      });
      if (data && data.success && data.valid) {
        setAppliedCoupon({
          code: data.couponCode,
          discountType: data.discountType || 'percentage',
          discountValue: data.discountValue || 0,
          maxDiscount: data.maxDiscount || Infinity,
          description: data.description
        });
        setSuccess('Coupon applied successfully! 🎉');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setCouponError(data.message || 'Invalid or expired coupon code');
      }
    } catch (err) {
      console.error('Coupon validation error:', err);
      setCouponError(err.response?.data?.error || err.message || 'Failed to validate coupon');
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const handlePayment = async () => {
    setIsProcessingPayment(true);
    setError('');

    const isScriptLoaded = await loadRazorpayScript();
    if (!isScriptLoaded) {
      setError('Razorpay SDK failed to load. Please check your internet connection.');
      setIsProcessingPayment(false);
      return;
    }

    try {
      const paymentPayload = {
          applicationId: draftId,
          amount: totalAmount,
          targetColleges: targetColleges,
          applicationData: applicationData,
          couponCode: appliedCoupon?.code || null,
          discountAmount: discount || 0
      };

      const orderResponse = await formFillApi.createPaymentOrder(paymentPayload);

      if (!orderResponse || !orderResponse.orderId) {
        throw new Error(orderResponse?.message || 'Failed to create payment order');
      }

      const options = {
        key: orderResponse.keyId || orderResponse.keyID || orderResponse.key,
        amount: orderResponse.amount,
        currency: orderResponse.currency || "INR",
        name: "Vidyazon Services",
        description: `Application Fee - ${application.personalDetails.fullName}`,
        order_id: orderResponse.orderId,
        handler: async function (response) {
          try {
            const verificationResponse = await formFillApi.verifyAndSubmit({
              applicationId: draftId,
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
              signature: response.razorpay_signature,
              targetColleges: targetColleges, 
            });

            if (verificationResponse) {
              localStorage.removeItem('pendingCollegeSelection');
              setSuccess('✅ Payment Successful! Colleges saved. Redirecting...');
              setTimeout(() => {
                navigate('/student/dashboard', {
                  state: { paymentSuccess: true, selectedColleges: targetColleges }
                });
              }, 2000);
            } else {
              setError('Payment verification failed. Please contact support.');
            }
          } catch (verifyErr) {
            console.error('Verification error:', verifyErr);
            setError('Payment verification failed: ' + verifyErr.message);
          } finally {
            setIsProcessingPayment(false);
          }
        },
        prefill: {
          name: application.personalDetails.fullName,
          email: application.contactDetails.email,
          contact: application.contactDetails.mobile
        },
        theme: {
          color: "#2563EB"
        },
        modal: {
          ondismiss: function() {
            setIsProcessingPayment(false);
          }
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.on('payment.failed', function (response) {
        setError(`Payment Failed: ${response.error.description}`);
        setIsProcessingPayment(false);
      });
      paymentObject.open();

    } catch (err) {
      console.error("Payment Error:", err);
      setError(err.message || 'Failed to initiate payment. Please try again.'); 
      setIsProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center pt-24 ${styles.bg}`}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className={styles.textSecondary}>Loading your application details...</p>
        </div>
      </div>
    );
  }

  if (!application || targetColleges.length === 0) {
    return (
      <div className={`min-h-screen flex items-center justify-center pt-24 ${styles.bg}`}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className={`${styles.textSecondary} mb-4`}>{error || 'No application data found'}</p>
          <button onClick={() => navigate('/student/dashboard')} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Go to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-4 sm:py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-500 ${styles.bg}`}>
      <div className="max-w-6xl mx-auto pt-40">
        
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/student-form', { state: { applicationData, draftId, selectedColleges: targetColleges } })}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm sm:text-base">Back to Application</span>
          </button>
          <h1 className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${styles.textPrimary}`}>Checkout</h1>
          <p className={`${styles.textSecondary} mt-1 text-sm sm:text-base`}>Review your order and complete payment</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-600 text-sm flex-1">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-xl flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-green-600 text-sm flex-1">{success}</p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Section - Order Details */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Selected Colleges Card */}
            <div className={`rounded-2xl shadow-lg p-6 border ${isDarkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-100'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-600 rounded-lg">
                    <GraduationCap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className={`text-xl font-bold ${styles.textPrimary}`}>Selected Colleges</h2>
                    <p className={`text-sm ${styles.textSecondary}`}>{targetColleges.length} colleges selected for application</p>
                  </div>
                </div>
                
                {/* FEE TIER BADGE */}
                <div className={`px-3 py-1 rounded-full text-xs font-bold border flex items-center gap-1
                  ${isDoubleFee 
                    ? 'bg-orange-100 text-orange-700 border-orange-200' 
                    : 'bg-green-100 text-green-700 border-green-200'
                  }`}>
                  <Layers className="w-3 h-3" />
                  {isDoubleFee ? 'Double Fee' : 'Single Fee'}
                </div>
              </div>
              
              <div className="space-y-2 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                {targetColleges.map((college, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border shadow-sm transition-shadow ${isDarkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-700' : 'bg-white border-blue-100 hover:shadow-md'}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {idx + 1}
                      </div>
                      <div className="min-w-0">
                        <p className={`font-semibold truncate ${styles.textPrimary}`}>{college.collegeName || college.name}</p>
                        {college.course && <p className={`text-xs ${styles.textSecondary}`}>{college.course}</p>}
                      </div>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>

            {/* Application Summary */}
            <div className={`rounded-2xl shadow-lg p-6 border transition-shadow hover:shadow-xl ${styles.cardBg}`}>
              <h2 className={`text-lg sm:text-xl font-bold mb-4 flex items-center gap-2 ${styles.textPrimary}`}>
                <Info className="w-5 h-5 text-blue-600" />
                Application Details
              </h2>
              <div className={`space-y-3 text-sm sm:text-base ${styles.textSecondary}`}>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span>Applicant Name:</span>
                  <span className={`font-medium ${styles.textPrimary}`}>{application.personalDetails.fullName}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span>Email:</span>
                  <span className={`font-medium ${styles.textPrimary} break-all`}>{application.contactDetails.email}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span>Mobile:</span>
                  <span className={`font-medium ${styles.textPrimary}`}>{application.contactDetails.mobile}</span>
                </div>
                {application.courseName && (
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span>Course:</span>
                    <span className={`font-medium ${styles.textPrimary}`}>{application.courseName}</span>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span>Application ID:</span>
                  <span className={`font-medium font-mono text-xs ${styles.textPrimary}`}>{draftId}</span>
                </div>
              </div>
            </div>

            {/* Coupon Section */}
            <div className={`rounded-2xl shadow-lg p-6 border transition-shadow hover:shadow-xl ${isDarkMode ? 'bg-purple-900/10 border-purple-800' : 'bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 border-purple-100'}`}>
              <h2 className={`text-lg sm:text-xl font-bold mb-4 flex items-center gap-2 ${styles.textPrimary}`}>
                <Tag className="w-5 h-5 text-purple-600" />
                Have a Coupon Code?
              </h2>
              
              {!appliedCoupon ? (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter coupon code"
                      className={`flex-1 px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent uppercase font-medium transition-all ${styles.inputBg}`}
                      disabled={isValidatingCoupon}
                    />
                    <button
                      onClick={handleApplyCoupon}
                      disabled={isValidatingCoupon}
                      className="w-full sm:w-auto px-8 py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isValidatingCoupon ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Validating...
                        </>
                      ) : (
                        'Apply'
                      )}
                    </button>
                  </div>
                  {couponError && (
                    <p className="text-red-600 text-sm flex items-center gap-2 bg-red-500/10 p-3 rounded-lg border border-red-500/20">
                      <AlertCircle className="w-4 h-4" />
                      {couponError}
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl p-5 border-2 border-green-500/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                        <Percent className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className={`font-bold text-lg ${styles.textPrimary}`}>{appliedCoupon.code}</p>
                        <p className="text-sm text-green-600 font-medium">
                          {appliedCoupon.discountType === 'fixed' 
                            ? `₹${appliedCoupon.discountValue} discount applied`
                            : `${appliedCoupon.discountValue}% discount applied`}
                        </p>
                        <p className="text-xs text-green-500 mt-1">
                          You saved ₹{discount.toFixed(2)}!
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleRemoveCoupon}
                      className="text-red-600 hover:text-red-700 font-bold text-sm bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-sm hover:shadow-md transition-all flex items-center gap-1"
                    >
                      <X className="w-4 h-4" />
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Security */}
            <div className={`rounded-2xl shadow-lg p-6 border transition-shadow hover:shadow-xl ${isDarkMode ? 'bg-blue-900/10 border-blue-800' : 'bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50 border-blue-100'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <h3 className={`font-bold text-lg ${styles.textPrimary}`}>Secure Payment</h3>
              </div>
              <p className={`text-sm mb-4 leading-relaxed ${styles.textSecondary}`}>
                Your payment is processed securely through <span className="font-semibold text-blue-600">Razorpay</span>. We never store your card details.
              </p>
            </div>
          </div>

          {/* Right Section - Price Summary */}
          <div className="lg:col-span-1">
            <div className={`rounded-2xl shadow-2xl p-6 border lg:sticky lg:top-28 ${styles.stickySummary}`}>
              <h2 className={`text-xl font-bold mb-6 flex items-center gap-2 ${styles.textPrimary}`}>
                <CreditCard className="w-5 h-5 text-blue-600" />
                Price Summary
              </h2>

              <div className="space-y-4 text-sm mb-6">
                <div className="flex justify-between items-center">
                  <span className={styles.textSecondary}>
                    Base Application Fee
                    {isDoubleFee && <span className="block text-xs text-orange-500">(Double Tier)</span>}
                  </span>
                  <span className={`font-semibold ${styles.textPrimary}`}>₹{BASE_FEE.toLocaleString('en-IN')}</span>
                </div>

                {appliedCoupon && (
                  <div className="flex justify-between items-center text-green-600 bg-green-500/10 -mx-2 px-2 py-2 rounded-lg">
                    <span className="flex items-center gap-1 font-medium">
                      <Tag className="w-3 h-3" />
                      Discount ({couponCode})
                    </span>
                    <span className="font-bold">-₹{discount.toFixed(2)}</span>
                  </div>
                )}

                {(PROCESSING_FEE > 0) && (
                  <div className="flex justify-between items-center">
                    <span className={styles.textSecondary}>Processing Fee</span>
                    <span className={`font-semibold ${styles.textPrimary}`}>₹{PROCESSING_FEE.toLocaleString('en-IN')}</span>
                  </div>
                )}

                {(PLATFORM_FEE > 0) && (
                  <div className="flex justify-between items-center">
                    <span className={styles.textSecondary}>Platform Fee</span>
                    <span className={`font-semibold ${styles.textPrimary}`}>₹{PLATFORM_FEE.toLocaleString('en-IN')}</span>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className={styles.textSecondary}>GST (18%)</span>
                  <span className={`font-semibold ${styles.textPrimary}`}>₹{gstAmount.toLocaleString('en-IN')}</span>
                </div>

                <div className={`border-t-2 pt-4 mt-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className={`text-base font-bold ${styles.textPrimary}`}>Total Amount</span>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-blue-600">₹{totalAmount.toLocaleString('en-IN')}</div>
                      {appliedCoupon && (
                        <div className="text-xs text-gray-500 line-through">
                          ₹{Math.round((BASE_FEE + discount + PROCESSING_FEE + PLATFORM_FEE) * 1.18).toLocaleString('en-IN')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handlePayment}
                disabled={isProcessingPayment}
                className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105 active:scale-95 duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isProcessingPayment ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5" />
                    Pay ₹{totalAmount.toLocaleString('en-IN')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;